import {
    IdCardSignupFormValues,
    IdCardSubscriptionCompanyUserGroup,
    IdCardSubscriptionDepartment,
    TransformedCompany,
} from "../../loginTypes"

export const transformCompanies = (companies: any[]): TransformedCompany[] => {
    const isOfficeHotelCompany = (
        company: any,
    ): company is {
        id: number
        name: string
        isOfficeHotel: boolean
        userGroups: IdCardSubscriptionCompanyUserGroup[]
    } => {
        return (
            typeof company.id === "number" &&
            typeof company.name === "string" &&
            "userGroups" in company &&
            Array.isArray(company.userGroups) &&
            "isOfficeHotel" in company &&
            company.isOfficeHotel
        )
    }

    const isStandardCompany = (
        company: any,
    ): company is {
        id: number
        name: string
        userGroups: IdCardSubscriptionCompanyUserGroup[]
        departments?: IdCardSubscriptionDepartment[]
    } => {
        return (
            typeof company.id === "number" &&
            typeof company.name === "string" &&
            "userGroups" in company &&
            Array.isArray(company.userGroups)
        )
    }

    const isSingleGroupCompany = (
        company: any,
    ): company is { id: number; name: string; userGroup: IdCardSubscriptionCompanyUserGroup } => {
        return (
            typeof company.id === "number" &&
            typeof company.name === "string" &&
            "userGroup" in company &&
            !("userGroups" in company)
        )
    }
    return companies.flatMap<TransformedCompany>((company) => {
        if (isOfficeHotelCompany(company) && company.userGroups.length) {
            return company.userGroups.map((group) => ({
                id: `group-${company.id}-${group.id}`,
                displayName: group.name,
                isGroup: true as const,
                companyId: company.id,
                userGroupId: group.id,
                signUpSettings: group.signUpSettings,
            }))
        }

        if (isStandardCompany(company)) {
            return [
                {
                    id: `company-${company.id}`,
                    displayName: company.name,
                    isGroup: false as const,
                    companyId: company.id,
                    userGroups: company.userGroups.map((group) => ({
                        ...group,
                        displayName: group.displayName ?? group.name,
                    })),
                    userGroup: undefined,
                    departments: company.departments ?? [],
                },
            ]
        }

        if (isSingleGroupCompany(company)) {
            return [
                {
                    id: `company-${company.id}`,
                    displayName: company.name,
                    isGroup: false as const,
                    companyId: company.id,
                    userGroup: company.userGroup,
                    userGroups: [],
                    departments: [],
                },
            ]
        }

        return []
    })
}

export const getCompanyDepartments = (
    company: TransformedCompany | undefined,
): ReadonlyArray<IdCardSubscriptionDepartment> => (company && !company.isGroup ? company.departments : [])

export type InitialCompanySelection = Readonly<{
    companyId: string | null
    userGroupId: string | null
}>

export const getInitialCompanySelection = (companies: TransformedCompany[]): InitialCompanySelection => {
    if (companies.length !== 1) {
        return { companyId: null, userGroupId: null }
    }

    const company = companies[0]

    if (company.isGroup) {
        return { companyId: company.id, userGroupId: company.userGroupId.toString() }
    }

    if (company.userGroup) {
        return { companyId: company.id, userGroupId: company.userGroup.id.toString() }
    }

    if (company.userGroups?.length === 1) {
        return { companyId: company.id, userGroupId: company.userGroups[0].id.toString() }
    }

    return { companyId: company.id, userGroupId: null }
}

export const prepareInitialValues = (companies: TransformedCompany[]): IdCardSignupFormValues => {
    return {
        displayName: "",
        mobileNumber: "",
        subscriptionId: null,
        ...getInitialCompanySelection(companies),
    }
}
